import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Grid,
  Switch,
} from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from "../../services/api"
import { API_ENDPOINTS } from "../../config/api"
import { useAuth } from '../../context/AuthContext';
// import { PERMISSIONS } from '../../utils/permissions';

export default function CreateRole() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [features, setFeatures] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.FEATURE.GET_ALL);
      setFeatures(response.data.features);
      
      // Initialize permissions with READ (view) enabled by default
      const initialPermissions = {};
      response.data.features.forEach(feature => {
        initialPermissions[feature.id] = { 
          read: true,  // Set READ to true by default
          create: false, 
          update: false, 
          delete: false 
        };
      });
      setPermissions(initialPermissions);
    } catch (err) {
      console.error('Error fetching features:', err);
      setError('Failed to fetch features. Please try again.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const roleResponse = await api.post(API_ENDPOINTS.ROLE.CREATE, {
        ...formData,
        client_id: user.clientId,
      });

      if (roleResponse.status === 200 || roleResponse.status === 201) {
        const roleId = roleResponse.data.role.id;

        for (const [featureId, featurePermissions] of Object.entries(permissions)) {
          const permissionMode = calculatePermissionMode(featurePermissions);
          if (permissionMode > 0) {
            await api.post(API_ENDPOINTS.ROLE_PERMISSION.CREATE, {
              role_id: roleId,
              feature_id: featureId,
              permission_mode: permissionMode,
            });
          }
        }
      } else {
        throw new Error(roleResponse.data.message[0]?.charAt(0).toUpperCase() + roleResponse.data.message[0]?.slice(1) + "." || "Failed to create role. Please try again.")
      }

      navigate('/roles');
    } catch (err) {
      console.error('Error creating role:', err);
      setError(err.response?.data?.message[0]?.charAt(0).toUpperCase() + err.response?.data?.message[0]?.slice(1) + "." || 'Failed to create role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculatePermissionMode = (featurePermissions) => {
    let mode = 0;
    if (featurePermissions.read) mode |= 1;
    if (featurePermissions.create) mode |= 2;
    if (featurePermissions.update) mode |= 4;
    if (featurePermissions.delete) mode |= 8;
    return mode;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handlePermissionChange = (featureId, permission) => {
    setPermissions(prevPermissions => ({
      ...prevPermissions,
      [featureId]: {
        ...prevPermissions[featureId],
        [permission]: !prevPermissions[featureId][permission],
      },
    }));
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    setPermissions(prevPermissions => {
      const newPermissions = {};
      for (const featureId in prevPermissions) {
        newPermissions[featureId] = {
          read: checked,
          create: checked,
          update: checked,
          delete: checked,
        };
      }
      return newPermissions;
    });
  };

  const handleClearAll = () => {
    setSelectAll(false);
    setPermissions(prevPermissions => {
      const newPermissions = {};
      for (const featureId in prevPermissions) {
        newPermissions[featureId] = {
          read: false,
          create: false,
          update: false,
          delete: false,
        };
      }
      return newPermissions;
    });
  };

  return (
    <Box sx={{ width: '100%', pl: 1, pr: 2, pt: 0, backgroundColor: 'background.paper', borderRadius: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton 
          onClick={() => navigate('/roles')}
          sx={{ 
            mr: 2, 
            color: 'text.secondary',
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
        >
          <ChevronLeft />
        </IconButton>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Create Role
        </Typography>
      </Box>

      <Paper 
        sx={{ 
          p: 2, 
          borderRadius: 6,
          boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
          backgroundColor: 'background.paper'
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <TextField
              required
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 8,
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#C41747',
                  },
                },
              }}
            />
          </Box>

          <Typography variant="h6" sx={{ mb: 2 }}>Feature Permissions</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={selectAll}
                  onChange={handleSelectAll}
                  color="primary"
                />
              }
              label="Select All Permissions"
            />
            <Button
              variant="outlined"
              onClick={handleClearAll}
              sx={{
                color: '#6C757D',
                borderColor: '#E0E0E0',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#6C757D',
                  bgcolor: 'transparent',
                },
              }}
            >
              Clear All
            </Button>
          </Box>
          <Grid container spacing={2}>
            {features.map((feature) => {
              // Show client feature only if user has all permissions for it (for now no one can set client permissions)
              if (feature.name === 'client') {
                // const hasAllClientPermissions = 
                //   user.permissions?.client === PERMISSIONS.ALL;
                // if (!hasAllClientPermissions) return null;
                return null;
              }

              return (
                <Grid item xs={12} sm={6} md={4} key={feature.id}>
                  <Card sx={{ backgroundColor: 'background.default' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>{feature.description}</Typography>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={permissions[feature.id]?.read || false}
                            onChange={() => handlePermissionChange(feature.id, 'read')}
                          />
                        }
                        label="View"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={permissions[feature.id]?.create || false}
                            onChange={() => handlePermissionChange(feature.id, 'create')}
                          />
                        }
                        label="Create"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={permissions[feature.id]?.update || false}
                            onChange={() => handlePermissionChange(feature.id, 'update')}
                          />
                        }
                        label="Update"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox 
                            checked={permissions[feature.id]?.delete || false}
                            onChange={() => handlePermissionChange(feature.id, 'delete')}
                          />
                        }
                        label="Delete"
                      />
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{
                bgcolor: '#E31B54',
                color: '#FFFFFF',
                textTransform: 'none',
                borderRadius: 18,
                px: 4,
                '&:hover': { bgcolor: '#C41747' },
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/roles')}
              sx={{
                color: "#F9826E",
                borderColor: "#F9826E",
                textTransform: "none",
                borderRadius: 18,
                "&:hover": {
                  color: "black",
                  borderColor: "#F7583D",
                  bgcolor: "#F9826E",
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
